import * as React from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { billingRepository } from "@/data/repositories";
import type { BillingInvoice, BillingProfile } from "@/types/billing";
import type { SubscriptionPlan } from "@/types/organization";

interface BillingContextValue { profile: BillingProfile | null; invoices: BillingInvoice[]; isLoading: boolean; error: string | null; changePlan: (plan: SubscriptionPlan) => Promise<void>; }
const BillingContext = React.createContext<BillingContextValue | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { activeOrganization } = useOrganizationContext();
  const [profile, setProfile] = React.useState<BillingProfile | null>(null);
  const [invoices, setInvoices] = React.useState<BillingInvoice[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let active = true;
    if (!activeOrganization) { setProfile(null); setInvoices([]); return undefined; }
    setIsLoading(true); setError(null);
    Promise.all([billingRepository.getProfile(activeOrganization.id), billingRepository.listInvoices(activeOrganization.id)])
      .then(([nextProfile, nextInvoices]) => { if (active) { setProfile(nextProfile); setInvoices(nextInvoices); } })
      .catch((cause: unknown) => active && setError(cause instanceof Error ? cause.message : "Could not load billing."))
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, [activeOrganization]);
  const changePlan = React.useCallback(async (plan: SubscriptionPlan) => {
    if (!activeOrganization) return;
    setProfile(await billingRepository.changePlan(activeOrganization.id, plan));
  }, [activeOrganization]);
  return <BillingContext.Provider value={{ profile, invoices, isLoading, error, changePlan }}>{children}</BillingContext.Provider>;
}
export function useBilling() { const value = React.useContext(BillingContext); if (!value) throw new Error("useBilling must be used within BillingProvider"); return value; }
